import type * as React from "react";

// TODO: migrate to react aria alternative
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { type VariantProps, cva } from "class-variance-authority";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-full effect-color disabled:cursor-not-allowed active:scale-[0.98] transition-transform duration-300 ease-out",
  {
    variants: {
      variant: {
        primary:
          "bg-inverse text-inverse data-[state=active]:bg-darkest tw-outline-none focus-visible:tw-outline-focus",
        secondary:
          "bg-dark-new text-default data-[state=active]:bg-[#EDEBE8] tw-outline-none focus-visible:tw-outline-focus",
        "ghost-inverse":
          "text-inverse ring-offset-darkest data-[state=active]:bg-overlay-light tw-outline-none focus-visible:tw-outline-focus",
        "secondary-inverse":
          "text-inverse bg-overlay-light ring-offset-darkest data-[state=active]:bg-overlay-lighter tw-outline-none focus-visible:tw-outline-focus",
        "outline-inverse":
          "bg-transparent border border-overlay-light text-inverse data-[state=active]:bg-default data-[state=active]:text-default data-[state=active]:border-inverse tw-outline-none focus-visible:tw-outline-focus",
        outline:
          "border text-default border-darkest data-[state=active]:border-overlay-darkest tw-outline-none focus-visible:tw-outline-focus",
        ghost:
          "bg-transparent text-default data-[state=active]:bg-dark-new tw-outline-none focus-visible:tw-outline-focus",
        "ghost-warm":
          "bg-transparent text-default rounded-[12px] data-[state=active]:bg-dark-new tw-outline-none focus-visible:tw-outline-focus",
        "ghost-subtle":
          "bg-transparent text-default data-[state=active]:bg-dark-new tw-outline-none focus-visible:tw-outline-focus",
        elevated:
          "bg-default rounded-full shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_0_rgba(0,0,0,0.04)] tw-outline-none focus-visible:tw-outline-focus",
      },
      expand: {
        true: "w-full",
        false: "w-fit",
      },
      size: {
        "sm-light": "h-24 f-ui-05",
        sm: "h-28 f-paragraph-03 px-12",
        md: "h-40 f-paragraph-02 px-16",
        "lg-compact": "h-44 f-paragraph-02 px-20",
        lg: "h-48 f-paragraph-02 px-20",
        "pill-sm": "w-24 h-24 f-ui-03",
        "pill-md": "w-32 h-32 f-ui-03",
        pill: "w-40 h-40 f-ui-03",
        "pill-lg": "size-48 f-ui-03",
        "pill-xl": "size-60 f-ui-03",
        "pill-responsive": "w-20 h-20 f-ui-03 md:w-32 md:h-32",
        "pill-responsive-lg": "w-28 h-28 md:w-40 md:h-40",
      },
      link: {
        true: "",
        false: "",
      },
      icon: {
        true: "",
        false: "",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "",
      },
      activePill: {
        true: "",
        false: "",
      },
      iconPos: {
        [ICON_ALIGN_OPTIONS.BEFORE]: "",
        [ICON_ALIGN_OPTIONS.AFTER]: "",
      },
    },
    compoundVariants: [
      {
        disabled: true,
        variant: "primary",
        className: "bg-darkest",
      },
      {
        disabled: true,
        variant: "elevated",
        className: "opacity-40",
      },
      // Icons
      {
        icon: false,
        size: ["sm-light"],
        className: "px-12",
      },
      {
        icon: true,
        size: ["lg", "lg-compact", "md"],
        className: "gap-x-12",
      },
      {
        icon: true,
        size: ["sm-light"],
        iconPos: ICON_ALIGN_OPTIONS.BEFORE,
        className: "pl-4 pr-8",
      },
      {
        icon: true,
        size: ["sm-light"],
        iconPos: ICON_ALIGN_OPTIONS.AFTER,
        className: "pr-4 pl-8 gap-6",
      },
      {
        icon: true,
        size: ["sm"],
        iconPos: ICON_ALIGN_OPTIONS.BEFORE,
        className: "pl-8 gap-6",
      },
      {
        icon: true,
        size: ["sm"],
        iconPos: ICON_ALIGN_OPTIONS.AFTER,
        className: "pr-8 gap-6",
      },
      // Hover
      {
        activePill: false,
        link: false,
        variant: "primary",
        className: "enabled:hover:bg-darkest",
      },
      {
        activePill: false,
        link: true,
        variant: "primary",
        className: "hover:bg-darkest",
      },
      {
        activePill: false,
        link: false,
        variant: "secondary",
        className: "enabled:hover:bg-[#EDEBE8]",
      },
      {
        activePill: false,
        link: true,
        variant: "secondary",
        className: "hover:bg-[#EDEBE8]",
      },
      {
        activePill: false,
        link: false,
        variant: "ghost-inverse",
        className: "enabled:hover:bg-overlay-light",
      },
      {
        activePill: false,
        link: true,
        variant: "ghost-inverse",
        className: "hover:bg-overlay-light",
      },
      {
        activePill: false,
        link: false,
        variant: "secondary-inverse",
        className: "enabled:hover:bg-overlay-lighter",
      },
      {
        activePill: false,
        link: true,
        variant: "secondary-inverse",
        className: "hover:bg-overlay-lighter",
      },
      {
        activePill: false,
        link: false,
        variant: "outline-inverse",
        className: "enabled:hover:bg-default enabled:hover:text-default",
      },
      {
        activePill: false,
        link: true,
        variant: "outline-inverse",
        className: "hover:bg-default hover:text-default",
      },
      {
        activePill: false,
        link: false,
        variant: "outline",
        className: "enabled:hover:text-overlay-darker-plus-2 enabled:hover:border-overlay-darkest",
      },
      {
        activePill: false,
        link: true,
        variant: "outline",
        className: "hover:text-overlay-darker-plus-2 hover:border-overlay-darkest",
      },
      {
        activePill: false,
        link: false,
        variant: "ghost",
        className: "enabled:hover:bg-[#E9E6E2]/50",
      },
      {
        activePill: false,
        link: true,
        variant: "ghost",
        className: "hover:bg-[#E9E6E2]/50",
      },
      {
        activePill: false,
        link: false,
        variant: "ghost-warm",
        className: "enabled:hover:tw-bg-cream",
      },
      {
        activePill: false,
        link: true,
        variant: "ghost-warm",
        className: "hover:tw-bg-cream",
      },
      {
        activePill: false,
        link: false,
        variant: "ghost-subtle",
        className: "enabled:hover:tw-bg-black/5",
      },
      {
        activePill: false,
        link: true,
        variant: "ghost-subtle",
        className: "hover:tw-bg-black/5",
      },
      {
        activePill: false,
        link: false,
        variant: "elevated",
        className: "enabled:hover:bg-dark",
      },
      {
        activePill: false,
        link: true,
        variant: "elevated",
        className: "hover:bg-dark",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      icon: false,
      iconPos: ICON_ALIGN_OPTIONS.AFTER,
      expand: false,
      link: false,
      disabled: false,
      activePill: false,
    },
  },
);

const activePillClassName = "group relative isolate overflow-hidden !bg-transparent !shadow-none";
const activePillOverlayClassName =
  "pointer-events-none absolute inset-0 -z-10 origin-center rounded-full transition-[transform,background-color,box-shadow,opacity] duration-300 ease-out motion-reduce:transition-none";
const activePillSelectedClassName =
  "scale-x-100 scale-y-100 bg-default opacity-100 shadow-[0_0_1px_rgb(0_0_0_/_0.4),0_4px_4px_rgb(0_0_0_/_0.04)]";
const activePillRestingClassName =
  "scale-x-[0.985] scale-y-[0.91] bg-transparent opacity-0 shadow-[0_0_1px_rgb(0_0_0_/_0),0_4px_4px_rgb(0_0_0_/_0)] group-hover:bg-overlay-light group-hover:opacity-100";

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
    Omit<VariantProps<typeof buttonVariants>, "activePill" | "icon" | "iconPos"> {
  active?: boolean;
  activePill?: boolean | null;
  asChild?: boolean;
  icon?: React.ReactNode;
  iconPosition?: ICON_ALIGN_OPTIONS;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({
  className,
  variant,
  size,
  icon,
  iconPosition = ICON_ALIGN_OPTIONS.AFTER,
  asChild = false,
  active = false,
  activePill = false,
  children,
  expand,
  link,
  disabled = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  const useActivePill = activePill === true && !asChild;

  return (
    <Comp
      className={cn(
        buttonVariants({
          variant,
          size,
          link,
          iconPos: iconPosition,
          icon: !!icon,
          expand,
          disabled,
          activePill: useActivePill,
        }),
        useActivePill && activePillClassName,
        useActivePill && active && "!text-default",
        className,
      )}
      disabled={disabled || false}
      ref={ref}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {useActivePill && (
            <span
              className={cn(
                activePillOverlayClassName,
                active ? activePillSelectedClassName : activePillRestingClassName,
              )}
            />
          )}
          {icon && iconPosition === ICON_ALIGN_OPTIONS.BEFORE && icon}
          {children}
          {icon && iconPosition === ICON_ALIGN_OPTIONS.AFTER && icon}
        </>
      )}
    </Comp>
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
